<template>
  <div class="mini-cards">
    <!-- Top Day(s) -->
    <div class="mini-card">
      <i class="bi bi-calendar-event main-icon" :style="{ color: zoneColor }" />
      <div class="mini-details">
        <h6 :style="{ color: zoneColor }">{{ $t('charts.highlights.topDays', { span: timeSpan }) }}</h6>
        <span class="badge">{{ $t('charts.highlights.visitors', { n: countText(maxDayCount) }) }}</span>
        <p class="explanation">
          <template v-if="days?.length">
            <span v-for="(d,i) in days" :key="d">
              {{ d }}<span v-if="i < days.length-1"> · </span>
            </span>
          </template>
          <template v-else>{{ $t('charts.highlights.noData') }}</template>
        </p>
      </div>
    </div>

    <!-- Top Hour(s) -->
    <div class="mini-card">
      <i class="bi bi-clock main-icon" :style="{ color: zoneColor }" />
      <div class="mini-details">
        <h6 :style="{ color: zoneColor }">{{ $t('charts.highlights.topHours', { span: timeSpan }) }}</h6>
        <span class="badge">{{ $t('charts.highlights.visitors', { n: countText(maxHourCount) }) }}</span>
        <p class="explanation">
          <template v-if="hours?.length">
            <span v-for="(h,i) in hours" :key="h">
              {{ h }}<span v-if="i < hours.length-1"> · </span>
            </span>
          </template>
          <template v-else>{{ $t('charts.highlights.noData') }}</template>
        </p>
      </div>
    </div>

    <!-- Busiest Avg Hour -->
    <div class="mini-card">
      <i class="bi bi-speedometer2 main-icon" :style="{ color: zoneColor }" />
      <div class="mini-details">
        <h6 :style="{ color: zoneColor }">{{ $t('charts.highlights.busiestAvgHour', { span: timeSpan }) }}</h6>
        <span class="badge">{{ $t('charts.highlights.avgVisitors', { n: countText(maxAvgVisitorsPerHour) }) }}</span>
        <p class="explanation">
          <template v-if="busiestAvgHours?.length">
            <span v-for="(h,i) in busiestAvgHours" :key="h">
              {{ h }}<span v-if="i < busiestAvgHours.length-1"> · </span>
            </span>
          </template>
          <template v-else>{{ $t('charts.highlights.noData') }}</template>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { currentLanguage } from '@/i18n'
import { formatNumber } from '@/i18n/format'

// English: exactly the old `n?.toLocaleString?.() || n`. Dutch: 1.234, whole visitors.
// (Interpolating undefined would print nothing in English, as before.)
const countText = (n) => currentLanguage() === 'en'
  ? (n?.toLocaleString?.() || n) ?? ''
  : formatNumber(n)

defineProps({
  days: Array,
  maxDayCount: Number,
  hours: Array,
  maxHourCount: Number,
  busiestAvgHours: Array,
  maxAvgVisitorsPerHour: Number,
  zoneColor: String,
  timeSpan: String,
})
</script>

<style scoped>
/* same wrapping behavior as the Trends widget */
.mini-cards{
  display:grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px;
}

.mini-card{
  display:flex;
  align-items:flex-start;
  background:#f7fafc;
  border:1px solid #e6edf3;
  border-radius:12px;
  padding:14px;
}

.main-icon{
  font-size:1.8rem;
  margin-right:.6rem;
  flex-shrink:0;
}

.mini-details{ flex:1 }

h6{
  margin:0 0 .35rem;
  font-size:1rem;
  font-weight:700;
}

.badge{
  display:inline-block;
  color:#fff;
  background:#4F7EB3;     /* overridden by inline style on Trends, here we keep neutral */
  border-radius:12px;
  padding:.18rem .5rem;
  font-size:.85rem;
  margin-bottom:.35rem;
}

.explanation{
  margin:0;
  font-size:.9rem;
  color:#6b7a86;
  line-height:1.3;
}
</style>